import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { CameraSet } from '../../types/canvision.types';

interface RotationProgressProps {
  currentSetIndex: number;
  totalSets: number;
  currentSet: CameraSet | null;
  isPlaying: boolean;
  timeRemaining: number;
  dwellTime: number;
  onDotClick?: (index: number) => void;
}

function RotationProgress({
  currentSetIndex,
  totalSets,
  currentSet,
  isPlaying,
  timeRemaining,
  dwellTime,
  onDotClick,
}: RotationProgressProps) {
  if (totalSets === 0) {
    return null;
  }

  const progress = isPlaying ? ((dwellTime - timeRemaining) / dwellTime) * 100 : 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {/* Set indicator */}
      <Typography variant="body2" color="text.secondary">
        Set {currentSetIndex + 1} of {totalSets}:
      </Typography>

      {/* Set name */}
      <Typography variant="body2" fontWeight="medium">
        {currentSet?.name || 'Unknown'}
      </Typography>

      {/* Dot indicators */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {Array.from({ length: totalSets }).map((_, idx) => (
          <Box
            key={idx}
            onClick={() => onDotClick?.(idx)}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: idx === currentSetIndex ? 'primary.main' : 'action.disabled',
              cursor: onDotClick ? 'pointer' : 'default',
              transition: 'background-color 0.2s',
              '&:hover': onDotClick
                ? {
                    backgroundColor: idx === currentSetIndex ? 'primary.dark' : 'action.selected',
                  }
                : {},
            }}
          />
        ))}
      </Box>

      {/* Countdown */}
      {isPlaying && totalSets > 1 && (
        <>
          <Typography variant="body2" color="text.secondary">
            Next in {timeRemaining}s
          </Typography>

          {/* Progress bar */}
          <Box sx={{ width: 100 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 4, borderRadius: 2 }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default RotationProgress;
